// Case-insensitive substring check, mirroring the backend's
// is_liberty_mutual_user (users/models/liberty.py in app-server). Kept in
// sync by hand -- there's no shared code between the two repos. "helmsman"
// was intentionally dropped (KAB-3474); see LIBERTY_USER_MARKERS.
export const LIBERTY_USER_MARKER = /liberty/i;

type LibertyCurrentUser = {
  email?: string | null;
  username?: string | null;
  organization_id?: number | null;
  user_type?: string | null;
} | null;

type LibertyIntakeUser = {
  email?: string | null;
  username?: string | null;
  libertyReferralFieldsEnabled?: boolean;
} | null;

type ComputeIsLibertyUserParams = {
  currentUser: LibertyCurrentUser;
  intakeToken: string | null;
  intakeUser: LibertyIntakeUser;
  isStaffLibertyFeatureEnabled: boolean;
};

const matchesLibertyMarker = (
  email?: string | null,
  username?: string | null,
) =>
  LIBERTY_USER_MARKER.test(email ?? "") ||
  LIBERTY_USER_MARKER.test(username ?? "");

// organization_id === 1 and user_type === "ORGANIZATION" identify staff
// as Liberty Mutual on their own -- mirrors the backend's
// is_liberty_mutual_user (users/models/liberty.py), which treats
// organization_id === 1 as sufficient for the ORGANIZATION branch, no
// email/username marker required. Applying this check only to the staff
// branch, not the intake-token branch, keeps that whole login path
// unaffected -- external referring parties who self-register through the
// intake portal are always user_type "INTAKE" and have no organization_id.
export const isStaffLibertyOrg = (currentUser: LibertyCurrentUser): boolean =>
  currentUser?.organization_id === 1 &&
  currentUser?.user_type === "ORGANIZATION";

// Two separate login flows can reach the intake wizard: staff logging in
// normally (currentUser, via useCurrentUser) and external referring
// parties logging in with the intake-specific token (intakeUser, via
// useIntakeToken) -- currentUser alone misses the second flow entirely,
// since it's never populated there. intakeUser is only consulted when
// intakeToken is actually present -- defense in depth in case a stale
// intakeUser is ever left in localStorage without a matching live token
// (e.g. a missed clear-on-logout elsewhere).
//
// ENABLE_LIBERTY_REFERRAL_FIELDS gates both paths, but each reads the flag
// from a different place: the staff path already has state.shared.constants
// populated (fetched whenever accessToken is present), while the
// intake-token path has no accessToken and so no way to fetch
// /api/v1/constants/ -- its flag value is piggybacked onto the
// intake_login response instead and stored on intakeUser.
export const computeIsLibertyUser = ({
  currentUser,
  intakeToken,
  intakeUser,
  isStaffLibertyFeatureEnabled,
}: ComputeIsLibertyUserParams): boolean => {
  const staffMatch =
    isStaffLibertyFeatureEnabled && isStaffLibertyOrg(currentUser);

  const intakeTokenMatch =
    Boolean(intakeToken) &&
    Boolean(intakeUser?.libertyReferralFieldsEnabled) &&
    matchesLibertyMarker(intakeUser?.email, intakeUser?.username);

  return staffMatch || intakeTokenMatch;
};
